import { Button } from '@bb/ui';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { TemplateVariable } from '../../../api/client.ts';
import { insertAtCursor } from './personalization.ts';
import { VariablePicker } from './VariablePicker.tsx';

type TextControl = HTMLInputElement | HTMLTextAreaElement;

export interface PersonalizationButtonProps {
  environmentId: string;
  /** The campaign being edited: its trigger data is offered too (see `VariablePicker`). */
  campaignId?: string;
  /** The text box the snippet goes into: its cursor (or selection) is where the snippet lands. */
  target: RefObject<TextControl | null>;
  /** The text box's value… */
  value: string;
  /** …and its change handler: called with the text with the snippet inserted. */
  onChange: (next: string) => void;
  /** Accessible name, e.g. «Personalize title» when a form has several fields. */
  label?: string;
  disabled?: boolean;
  /** After an insertion, with the variable chosen. */
  onInserted?: (variable: TemplateVariable) => void;
  /** Variables of this field only, listed first (see `VariablePicker`). */
  extraVariables?: readonly TemplateVariable[];
}

/**
 * The personalization button of a text field (BB-54): opens the variable picker and inserts the chosen variable's
 * snippet (`{{${first_name} | default: ''}}`) at the cursor of the target text box, replacing a selection; the
 * cursor ends up right after the snippet. Escape closes the picker.
 */
export function PersonalizationButton({ environmentId, campaignId, target, value, onChange, label = 'Insert personalization', disabled = false, onInserted, extraVariables }: PersonalizationButtonProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  /** Where the cursor was when the text box lost focus; unknown until it has had focus (then: the end). */
  const selection = useRef<{ start: number | null; end: number | null } | null>(null);
  const caret = useRef<number | null>(null);

  // Re-attached on every render: the composer may swap the text box (an input that grows into a textarea).
  useEffect(() => {
    const element = target.current;
    if (!element) return undefined;
    const remember = () => {
      selection.current = { start: element.selectionStart, end: element.selectionEnd };
    };
    element.addEventListener('blur', remember);
    return () => element.removeEventListener('blur', remember);
  });

  // Once the new value is in the text box, the cursor goes after the snippet.
  useLayoutEffect(() => {
    const element = target.current;
    if (caret.current === null || !element || element.value !== value) return;
    element.focus();
    element.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [value, target]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  const cursor = () => {
    const element = target.current;
    if (element && document.activeElement === element) return { start: element.selectionStart, end: element.selectionEnd };
    return selection.current ?? { start: null, end: null };
  };

  const insertText = (text: string) => {
    const { start, end } = cursor();
    const next = insertAtCursor(value, text, start, end);
    caret.current = next.caret;
    selection.current = { start: next.caret, end: next.caret };
    setOpen(false);
    onChange(next.value);
  };
  const insert = (variable: TemplateVariable) => {
    insertText(variable.liquid);
    onInserted?.(variable);
  };

  return (
    <span className="personalization" ref={root}>
      <Button
        ref={trigger}
        size="sm"
        variant="ghost"
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        icon={<span aria-hidden="true">{'{+}'}</span>}
        onClick={() => setOpen((was) => !was)}
      />
      {open && (
        <div
          className="personalization__panel"
          role="dialog"
          aria-label={label}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
              trigger.current?.focus();
            }
          }}
        >
          <VariablePicker environmentId={environmentId} campaignId={campaignId} onSelect={insert} onInsertText={insertText} autoFocus {...(extraVariables ? { extraVariables } : {})} />
        </div>
      )}
    </span>
  );
}
